import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { ArrowLeft, Building2, MapPin, Loader2, LayoutDashboard, Settings, FileText, Navigation, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

import CustomerLayout from '../../CustomerLayout';
import NewWorkOrderSlideOver from '../../jobs/NewWorkOrderSlideOver';

import OverviewTab   from './tabs/OverviewTab';
import DetailsTab    from './tabs/DetailsTab';
import PostsTab      from './tabs/PostsTab';
import DocumentsTab  from './tabs/DocumentsTab';

import { getFacilityById, getJobsByFacility } from '../../../../../services/staffingService';

const TABS = [
  { key: 'overview',   label: 'Overview',   Icon: LayoutDashboard },
  { key: 'details',    label: 'Details',    Icon: Settings        },
  { key: 'posts',      label: 'Posts',      Icon: Navigation      },
  { key: 'documents',  label: 'Documents',  Icon: FileText        },
];

const STATUS_BADGE = {
  active:   { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Active'   },
  inactive: { bg: 'bg-gray-100',    text: 'text-gray-600',    label: 'Inactive' },
};

// Deduplicate shift templates from a job for "repeat order" pre-fill.
// The facility overview's job list may not carry full shift arrays,
// so shiftTemplates gracefully degrades to [] (user adds shifts manually).
function extractHHMM(isoString, timezone) {
  if (!isoString) return '08:00';
  const tz = timezone || 'America/New_York';
  const result = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
  }).format(new Date(isoString));
  return result.replace(/^24:/, '00:');
}

function buildFacilityPrefillData(job) {
  if (!job) return null;
  const seen = new Set();
  const shiftTemplates = (job.shifts || [])
    .map(s => ({
      startTime:      extractHHMM(s.startTime, job.timezone),
      endTime:        extractHHMM(s.endTime,   job.timezone),
      staffsRequired: s.staffsRequired || 1,
    }))
    .filter(t => {
      const key = `${t.startTime}|${t.endTime}|${t.staffsRequired}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 5);

  return {
    serviceType:   job.serviceType   || '',
    description:   job.memo          || '',
    hourlyPayRate: job.hourlyPayRate  || '',
    facilityId:    job.facilityId    || '',
    shiftTemplates,
  };
}

/**
 * FacilityDetail — MASTER ORCHESTRATOR for the facility detail page.
 *
 * All service calls live here. Tab children receive data via props and fire
 * callbacks (onFacilityUpdate, onFileAdd, onFileRemove) to trigger state updates.
 *
 * URL: /customer-company/:customerCompanyId/facilities/:facilityId
 */
const FacilityDetail = () => {
  const { customerCompanyId, facilityId } = useParams();
  const history = useHistory();

  const [facility,     setFacility]     = useState(null);
  const [jobs,         setJobs]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [jobsLoading,  setJobsLoading]  = useState(true);
  const [activeTab,    setActiveTab]    = useState('overview');
  const [workOrderOpen, setWorkOrderOpen] = useState(false);
  const [repeatJob,    setRepeatJob]    = useState(null);  // job to repeat

  // ── Fetch facility ──────────────────────────────────────────────────────────
  const fetchFacility = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getFacilityById(customerCompanyId, facilityId);
      setFacility(resp.data);
    } catch {
      toast.error('Failed to load facility');
    } finally {
      setLoading(false);
    }
  }, [customerCompanyId, facilityId]);

  // ── Fetch jobs for this facility ────────────────────────────────────────────
  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const resp = await getJobsByFacility(customerCompanyId, facilityId);
      setJobs(resp.data || []);
    } catch {
      // non-fatal — overview just shows empty
    } finally {
      setJobsLoading(false);
    }
  }, [customerCompanyId, facilityId]);

  useEffect(() => {
    fetchFacility();
    fetchJobs();
  }, [fetchFacility, fetchJobs]);

  // ── Callbacks passed to tabs ─────────────────────────────────────────────────

  // DetailsTab + PostsTab PATCH → merge updated facility into state
  const handleFacilityUpdate = useCallback((updated) => {
    setFacility(updated);
  }, []);

  // DocumentsTab upload → append new file
  const handleFileAdd = useCallback((newFile) => {
    setFacility(prev => ({ ...prev, files: [...(prev.files || []), newFile] }));
  }, []);

  // DocumentsTab delete → remove file by id
  const handleFileRemove = useCallback((fileId) => {
    setFacility(prev => ({ ...prev, files: (prev.files || []).filter(f => f._id !== fileId) }));
  }, []);

  // NewWorkOrderSlideOver closed → re-fetch jobs
  const handleWorkOrderCreated = useCallback(() => {
    setWorkOrderOpen(false);
    setRepeatJob(null);
    fetchJobs();
  }, [fetchJobs]);

  // Called by OverviewTab when user clicks "Repeat Order" on a job row
  const handleRepeatOrder = useCallback((job) => {
    setRepeatJob(job);
    setWorkOrderOpen(true);
  }, []);

  if (loading) {
    return (
      <CustomerLayout>
        <div className="flex h-full items-center justify-center">
          <Loader2 size={32} className="animate-spin text-muted-foreground" />
        </div>
      </CustomerLayout>
    );
  }

  if (!facility) return null;

  const statusCfg = STATUS_BADGE[facility.status] || STATUS_BADGE.active;

  return (
    <CustomerLayout>
      <div className="flex h-full flex-col overflow-hidden">

        {/* ── Top bar ────────────────────────────────────────────────────────── */}
        <div className="border-b border-border bg-card px-6 py-4 flex-shrink-0">
          <button
            onClick={() => history.push(`/customer-company/${customerCompanyId}/facilities`)}
            className="mb-3 flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={14} /> Back to Facilities
          </button>

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#404880]/10 flex-shrink-0">
                <Building2 size={18} className="text-[#404880]" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">{facility.name}</h1>
                <p className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                  <MapPin size={10} />
                  {[facility.address, facility.city, facility.state].filter(Boolean).join(', ') || 'No address'}
                  {facility.facilityType && <> &middot; {facility.facilityType}</>}
                </p>
              </div>
            </div>
            <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${statusCfg.bg} ${statusCfg.text}`}>
              {statusCfg.label}
            </span>
          </div>

          {/* Tab bar */}
          <div className="mt-4 flex gap-1">
            {TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`relative flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors rounded-t-lg ${
                  activeTab === key ? 'text-[#CC457D]' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon size={13} /> {label}
                {activeTab === key && (
                  <motion.div
                    layoutId="facility-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-[#CC457D]"
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab content ─────────────────────────────────────────────────────── */}
        <div className={`flex-1 overflow-y-auto ${activeTab === 'posts' ? 'overflow-hidden flex flex-col' : ''}`}>
          {activeTab === 'overview' && (
            <OverviewTab
              facility={facility}
              jobs={jobsLoading ? [] : jobs}
              onNewWorkOrder={() => { setRepeatJob(null); setWorkOrderOpen(true); }}
              onRepeatOrder={handleRepeatOrder}
            />
          )}

          {activeTab === 'details' && (
            <DetailsTab
              facility={facility}
              customerCompanyId={customerCompanyId}
              onFacilityUpdate={handleFacilityUpdate}
            />
          )}

          {activeTab === 'posts' && (
            <PostsTab
              facility={facility}
              customerCompanyId={customerCompanyId}
              onFacilityUpdate={handleFacilityUpdate}
            />
          )}

          {activeTab === 'documents' && (
            <DocumentsTab
              facility={facility}
              customerCompanyId={customerCompanyId}
              onFileAdd={handleFileAdd}
              onFileRemove={handleFileRemove}
            />
          )}
        </div>

      </div>

      {/* New Work Order slide-over (also handles repeat orders) */}
      {workOrderOpen && (
        <NewWorkOrderSlideOver
          open={workOrderOpen}
          onClose={() => { setWorkOrderOpen(false); setRepeatJob(null); }}
          onCreated={handleWorkOrderCreated}
          customerCompanyId={customerCompanyId}
          initialFacilityId={facilityId}
          prefillData={repeatJob ? buildFacilityPrefillData(repeatJob) : null}
        />
      )}
    </CustomerLayout>
  );
};

export default FacilityDetail;
